import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { AlertBanner, InlineResult, Skeleton } from "../../components/ui/Feedback";
import {
  DestructiveConfirmDialog,
  ReauthenticationDialog,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { operationsApi, securityApi } from "../../lib/api";

export function RetentionPanel({ projectId }: { readonly projectId: string }) {
  const auth = useAuth();
  const preview = useQuery({
    queryKey: ["operations-retention", projectId],
    queryFn: ({ signal }) => operationsApi.retentionPreview(projectId, signal),
    retry: false,
  });
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [error, setError] = useState<string>();
  const [jobId, setJobId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const verify = async (credentials: ReauthenticationCredentials) => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (preview.data?.state !== "ready" || reauthenticationId === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await operationsApi.applyRetention({
        projectId,
        previewDigest: preview.data.digest,
        confirmationProjectId: projectId,
        reauthenticationId,
      });
      setJobId(result.jobId);
      setReauthenticationId(undefined);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const disabledReason = preview.data?.state === "disabled" ? preview.data.reason : undefined;
  return (
    <section aria-labelledby="retention-heading" className="card stack">
      <div>
        <p className="eyebrow">Saklama politikası</p>
        <h2 id="retention-heading">Temizlik önizlemesi</h2>
        <p className="muted">
          Temizlik yalnızca sahip politikası, doğrulanmış adaylar ve sunucunun tamamladığını
          bildirdiği iş sonucu ile geçerlidir.
        </p>
      </div>
      <AlertBanner heading="Yedekler düz metin olarak ayrı tutulur" tone="info">
        <p>
          Yedek sahibinin sorumluluğundadır. İzolasyon, sağlama toplamı, tarama ve yeni hedef
          doğrulaması görünür olmalıdır; bu panel şifreleme, anahtar veya kurtarma ayarı sunmaz.
        </p>
      </AlertBanner>
      {preview.isLoading ? <Skeleton label="Saklama önizlemesi yükleniyor" /> : null}
      {preview.isError ? (
        <AlertBanner heading="Saklama önizlemesi alınamadı" tone="error">
          <p>{apiErrorMessage(preview.error)}</p>
        </AlertBanner>
      ) : null}
      {disabledReason === undefined ? null : (
        <AlertBanner heading="Temizlik kapalı" tone="warning">
          <p>
            {disabledReason === "owner_policy_required"
              ? "Geçerli bir sahip saklama politikası yok. Bu nedenle temizlik işlemi başlatılamaz."
              : "Geçerli politika var, ancak yetkili canlı aday envanteri doğrulanamadı. Temizlik işlemi başlatılamaz."}
          </p>
        </AlertBanner>
      )}
      {preview.data?.state !== "ready" ? null : (
        <>
          <dl className="definition-grid">
            <div>
              <dt>Aday</dt>
              <dd>{preview.data.candidateIds.length}</dd>
            </div>
            <div>
              <dt>Toplam</dt>
              <dd>
                {new Intl.NumberFormat("tr-TR", {
                  style: "unit",
                  unit: "byte",
                  unitDisplay: "long",
                }).format(preview.data.totalBytes)}
              </dd>
            </div>
            <div>
              <dt>Önizleme özeti</dt>
              <dd>
                <code>{preview.data.digest}</code>
              </dd>
            </div>
          </dl>
          <p>Bu önizleme silme tamamlandı anlamına gelmez. Onaydan sonra iş durumunu takip edin.</p>
          <button
            className="button button--destructive"
            onClick={() => setReauthenticationId("")}
            type="button"
          >
            Temizlik için yeniden doğrula
          </button>
        </>
      )}
      {jobId === undefined ? null : (
        <InlineResult requestId={jobId} tone="info">
          Temizlik isteği kabul edildi. İş sonucu doğrulanana kadar hiçbir öğenin silindiği iddia
          edilmez.
        </InlineResult>
      )}
      {preview.data?.state !== "ready" ||
      reauthenticationId === undefined ? null : reauthenticationId === "" ? (
        <ReauthenticationDialog
          actionName="saklama temizliğini uygula"
          error={error}
          onOpenChange={(open) => {
            if (!open) setReauthenticationId(undefined);
          }}
          onVerify={verify}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
          submitting={busy}
        />
      ) : (
        <DestructiveConfirmDialog
          confirmationLabel={projectId}
          consequence="Yalnızca bu önizlemedeki doğrulanmış adaylar sunucu tarafından yeniden denetlenmek üzere kuyruğa alınacak."
          error={error}
          finalAction="Temizlik isteğini gönder"
          name="Saklama temizliğini uygula"
          onConfirm={apply}
          onOpenChange={(open) => {
            if (!open) setReauthenticationId(undefined);
          }}
          open
          submitting={busy}
        />
      )}
    </section>
  );
}
